import type { CraftingComponent } from "@/lib/crafting/types";
import { assetUrl } from "@/lib/bungie/assets";
import { isEmptySocketComponent } from "@/lib/crafting/engine";

export interface ComponentTileProps {
  component: CraftingComponent;
  selected: boolean;
  /** True when this plug is on the real weapon right now (reshape mode). */
  equipped?: boolean;
  /** The player's current weapon level, for the locked treatment. */
  currentLevel: number;
  onSelect: () => void;
  /**
   * Called when this tile becomes the thing to describe — on hover or focus.
   * Deliberately has no "stopped previewing" counterpart: clearing on every
   * mouseleave made the detail panel flicker back to the selected perk as the
   * pointer crossed the gaps between tiles. The grid clears it once, on leaving
   * the grid entirely.
   */
  onPreview?: () => void;
}

/**
 * One perk in the grid.
 *
 * Locked options stay fully selectable — picking one raises the target level
 * rather than blocking, which is the whole point of a planner. Every state is
 * carried by a badge or an outline as well as colour, and is named in the
 * accessible label, so none of it is conveyed by colour alone.
 */
export function ComponentTile({
  component,
  selected,
  equipped,
  currentLevel,
  onSelect,
  onPreview,
}: ComponentTileProps) {
  const locked = component.requiredLevel > currentLevel;
  const icon = assetUrl(component.icon);
  const isEmpty = isEmptySocketComponent(component);

  const stateWords = [
    component.enhanced ? "enhanced" : null,
    equipped ? "currently equipped" : null,
    locked ? `locked until weapon level ${component.requiredLevel}` : "available now",
    selected ? "selected" : null,
  ].filter(Boolean);

  return (
    <button
      type="button"
      onClick={onSelect}
      onFocus={onPreview}
      onMouseEnter={onPreview}
      aria-pressed={selected}
      aria-label={`${component.name} — ${stateWords.join(", ")}`}
      title={component.name}
      className={[
        "corner-brackets group relative grid h-16 w-16 place-items-center",
        "transition-colors duration-150 ease-(--ease-crisp)",
        selected ? "bg-white/16" : "bg-white/[0.02] hover:bg-white/10",
      ].join(" ")}
      style={selected ? undefined : { ["--tw-content" as string]: undefined }}
    >
      {/* Selection outline — a full box, distinct from the always-on brackets. */}
      {selected && (
        <span aria-hidden className="pointer-events-none absolute inset-0 border border-line" />
      )}

      <span
        className={[
          "relative grid h-12 w-12 place-items-center overflow-hidden rounded-full",
          // An empty socket is "no component", not a perk — a dashed outline,
          // matching the empty memento slot, rather than a filled disc.
          isEmpty ? "border border-dashed border-line-dim" : "ring-1 ring-white/25",
          locked ? "opacity-45 grayscale-[0.3]" : "",
        ].join(" ")}
      >
        {isEmpty ? (
          <span aria-hidden className="text-[0.6rem] leading-none text-ink-3">
            None
          </span>
        ) : icon ? (
          <img src={icon} alt="" aria-hidden className="h-full w-full object-cover" />
        ) : (
          <span aria-hidden className="h-full w-full bg-perk-deep" />
        )}

        {/* Enhanced: gold wash rising from the bottom of the disc. */}
        {component.enhanced && (
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background:
                "linear-gradient(to top, var(--color-enhanced) 0%, color-mix(in srgb, var(--color-enhanced-hi) 55%, transparent) 38%, transparent 68%)",
            }}
          />
        )}
      </span>

      {/* Enhanced badge, top-left. */}
      {component.enhanced && (
        <span
          aria-hidden
          className="absolute top-0.5 left-0.5 text-[0.7rem] leading-none font-bold text-enhanced"
        >
          ↑
        </span>
      )}

      {/* Locked badge, bottom-right. */}
      {locked && (
        <span
          aria-hidden
          className="absolute right-0.5 bottom-0.5 grid h-4 min-w-4 place-items-center bg-void/85 px-0.5 text-[0.6rem] leading-none font-semibold text-ink-2"
          title={`Requires level ${component.requiredLevel}`}
        >
          🔒{component.requiredLevel}
        </span>
      )}

      {/* Equipped marker — the game relies on slot position; we make it explicit. */}
      {equipped && !selected && (
        <span
          aria-hidden
          className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-ok"
          title="Currently equipped"
        />
      )}
    </button>
  );
}
